// Authentication context for Studio1 Alpha
// Implements user session management with WorkOS via backend cookies

import React, { createContext, useContext, useEffect, useState } from "react";
import { logger } from "../services/logger";
import { generateUUID } from "../utils/uuid";

const BACKEND_API_URL =
  import.meta.env.VITE_BACKEND_API_URL || "http://localhost:3000";

// User interface matching WorkOS /auth/me response
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  profilePictureUrl?: string;
  emailVerified?: boolean;
  // Terms of Service acceptance. The API returns these on /v1/auth/me and
  // redirects users who have NOT accepted to /accept-terms. `refreshUser()`
  // maps the whole payload via `setUser(data.data)`, so these flow through
  // automatically — they are declared here for type-safety at call sites.
  hasAcceptedTos?: boolean;
  acceptedTosAt?: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    const correlationId = generateUUID();

    try {
      // Send X-Correlation-ID so the API and DB log the same id for this
      // request — matches the apiClient interceptor at apiService.ts:47.
      // Phase 1 of docs/plans/understandable-error-messages.md requires
      // every frontend-originated request to carry a correlation id.
      const response = await fetch(`${BACKEND_API_URL}/v1/auth/me`, {
        credentials: "include",
        headers: {
          "X-Correlation-ID": correlationId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.data);

        logger.info("User session restored", {
          userId: data.data.id,
          email: data.data.email,
          correlationId,
        });
      } else {
        // 401 means not authenticated - clear user, let ProtectedRoute redirect to /auth
        setUser(null);
        logger.debug("User not authenticated", {
          status: response.status,
          correlationId,
        });
      }
    } catch (error) {
      logger.error("Failed to fetch user", error as Error, {
        correlationId,
      });
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signOut = () => {
    const correlationId = generateUUID();

    logger.info("User signing out", {
      userId: user?.id,
      correlationId,
    });

    // Redirect to backend logout endpoint
    // Backend will clear the cookie and redirect to /auth
    window.location.href = `${BACKEND_API_URL}/v1/auth/logout`;
  };

  // Fetch user on mount
  useEffect(() => {
    refreshUser();
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    signOut,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
